import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { TaskCardType } from '@econtractor/types';
import * as apiTask from 'api/apiTask';
import { useToast } from 'context/ToastContext';
import SolarIcon from 'components/icons/SolarIcon';
import spinnerTitle from 'components/others/spinnerTitle';
import TaskCategorySelect from '../../../TaskCategory/TaskCategorySelect';

interface TaskCardCategorySectionProps {
    task: TaskCardType;
    canEdit: boolean;
    onSaved: (task: TaskCardType) => void;
}

const TaskCardCategorySection = ({ task, canEdit, onSaved }: TaskCardCategorySectionProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { showToast } = useToast();
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [draftId, setDraftId] = useState<number | null>(task.categoryId ?? null);

    useEffect(() => {
        if (!canEdit) setEditing(false);
    }, [canEdit]);

    useEffect(() => {
        if (editing) return;
        setDraftId(task.categoryId ?? null);
    }, [editing, task.ID, task.categoryId]);

    const startEdit = () => {
        setDraftId(task.categoryId ?? null);
        setEditing(true);
    };

    const handleSave = async () => {
        if (!canEdit || saving) return;
        const next = draftId ?? null;
        if ((next || 0) === Number(task.categoryId || 0)) {
            setEditing(false);
            return;
        }
        setSaving(true);
        const result = await apiTask.updateTaskCategory(task.ID, { categoryId: next });
        setSaving(false);
        if (!result?.stat || !result.task) {
            showToast(t('common:messError'), 'danger');
            return;
        }
        showToast(t('messCategoryUpdated'), 'success');
        setEditing(false);
        onSaved(result.task);
    };

    const categoryName = String(task.categoryName || '').trim();

    return (
        <section className="task-card-section mb-3">
            <div className="task-card-section__label">{t('fields.category')}</div>
            {editing ? (
                <div className="task-card-panel">
                    <div className="mb-3">
                        <TaskCategorySelect
                            value={draftId}
                            disabled={saving}
                            allowCreate={false}
                            onChange={(opt) => setDraftId(opt?.value ?? null)}
                        />
                    </div>
                    <div className="text-end">
                        <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="mb-1 min70px"
                            style={{ height: 24 }}
                            disabled={saving}
                            onClick={() => setEditing(false)}
                        >
                            {t('common:buttons.cancel')}
                        </Button>
                        <Button
                            type="button"
                            variant="success"
                            size="sm"
                            className="mb-1 ms-2 min70px"
                            style={{ height: 24 }}
                            disabled={saving}
                            onClick={() => void handleSave()}
                        >
                            {spinnerTitle(t('common:buttons.change'), saving, t('common:buttons.saving'))}
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="task-card-panel">
                    <div className="d-flex align-items-center justify-content-between gap-2">
                        <span className={categoryName ? 'text-body' : 'text-muted'}>
                            {categoryName || '—'}
                        </span>
                        {canEdit && (
                            <button
                                type="button"
                                className="btn btn-link p-0 align-baseline fs-12 flex-shrink-0"
                                onClick={startEdit}
                            >
                                <SolarIcon name="pencil" className="me-1 fs-14" />
                                {t('common:buttons.change')}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
};

export default TaskCardCategorySection;
